import React from "react";
import { Container, Title, Paper, Group, Button, Table, Alert, Stack, Select } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import api from "../services/ApiService";
import { useTrans } from "core";
import { Api_User, Api_Report } from "../../../constants/SubApi.js";
import getLocalizedField from "services/getLocalizedField.js";

export default function ReportScreen() {
	const { t } = useTrans();
	const [owners, setOwners] = React.useState([]);
	const [ownerId, setOwnerId] = React.useState(null);
	const [startDate, setStartDate] = React.useState(null);
	const [endDate, setEndDate] = React.useState(null);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState(null);
	const [rows, setRows] = React.useState([]);

	React.useEffect(() => {
		const loadOwners = async () => {
			try {
				const res = await api.get(`/${Api_User}`);
				if (res.success && Array.isArray(res.data)) {
					setOwners(
						res.data.map((o) => ({
							value: o.id.toString(),
							label: `${o.name}`,
						}))
					);
				}
			} catch (err) {
				console.error(err);
			}
		};
		loadOwners();
	}, []);

	const buildQuery = () => {
		const params = new URLSearchParams();
		if (ownerId) params.append("ownerId", ownerId);
		if (startDate) params.append("startDate", new Date(startDate).getTime());
		if (endDate) params.append("endDate", new Date(endDate).getTime());
		const query = params.toString();
		return query ? `?${query}` : "";
	};

	const loadReport = async () => {
		setError(null);

		if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
			notifications.show({
				title: t("messages.error") || "Error",
				message: t("report.invalidDate") || "End date cannot be before start date.",
				color: "red",
			});
			return;
		}

		setLoading(true);

		try {
			const q = buildQuery();
			const res = await api.get(`/${Api_Report}${q}`);

			const data = res?.data || [];
			setRows(
				data.map((r) => ({
					ownerId: r.ownerId,
					ownerName: r.ownerName || "-",
					currencyName: getLocalizedField(r, "currency_name") || "",
					totalValue: Number(r.totalValue) || 0,
					reservationsCount: Number(r.reservationsCount) || 0,
				}))
			);
		} catch (err) {
			console.error(err);
			setError(err?.data?.message || err.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container size="md" my={20}>
			<Title order={2} mb={16}>
				{t("report.title")}
			</Title>

			<Paper shadow="sm" p="md" mb="lg">
				<Stack>
					{error && <Alert color="red">{error}</Alert>}

					<Group grow>
						<Select
							label={t("report.owner")}
							placeholder={t("report.selectOwner")}
							data={owners}
							value={ownerId}
							onChange={setOwnerId}
							clearable
							searchable
						/>

						<DateInput label={t("report.fromDate") || "From"} value={startDate} onChange={setStartDate} clearable />

						<DateInput label={t("report.toDate") || "To"} value={endDate} onChange={setEndDate} clearable />
					</Group>

					<Group justify="flex-end">
						<Button onClick={loadReport} loading={loading}>
							{t("report.generate")}
						</Button>
					</Group>
				</Stack>
			</Paper>

			<Paper shadow="md" radius="md" p="md">
				<Table
					withTableBorder
					withColumnBorders
					highlightOnHover
					verticalSpacing="sm"
					horizontalSpacing="md"
					striped
					style={{
						borderRadius: 8,
						overflow: "hidden",
						border: "1px solid #ddd",
					}}
				>
					<thead style={{ background: "#f8f9fa" }}>
						<tr>
							<th style={{ borderRight: "1px solid #e0e0e0" }}>{t("report.owner")}</th>
							<th style={{ borderRight: "1px solid #e0e0e0" }}>{t("report.totalValue")}</th>
							<th>{t("report.reservationsCount")}</th>
						</tr>
					</thead>

					<tbody>
						{rows.map((r, i) => (
							<tr key={i} style={{ borderBottom: "1px solid #e0e0e0" }}>
								<td style={{ borderRight: "1px solid #e0e0e0" }}>{r.ownerName}</td>
								<td style={{ borderRight: "1px solid #e0e0e0" }}>
									{/* Show value and currency together */}
									{r.totalValue.toFixed(2)} {r.currencyName}
								</td>
								<td>{r.reservationsCount}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</Paper>
		</Container>
	);
}
